import React, { useState } from 'react';
import { Box, Typography, TextField, FormControl, RadioGroup, Paper, FormControlLabel, Radio, Button, CircularProgress } from '@mui/material';
import { QrCode as QrCodeIcon, Sms as SmsIcon } from '@mui/icons-material';
import { validateAadhaar } from '../../../../helpers/abdm-helper';

const AadhaarEntryForm = ({
  aadhaarNumber,
  setAadhaarNumber,
  authenticationType,
  setAuthenticationType,
  onContinue,
  isLoading
}) => {
  const [touched, setTouched] = useState(false);

  const getErrorMessage = () => {
    if (!touched && !aadhaarNumber) return '';

    if (aadhaarNumber.length < 12) {
      return 'Aadhaar Number must be 12 digits';
    }

    if (aadhaarNumber.length === 12) {
      const { isValid } = validateAadhaar(aadhaarNumber);
      if (!isValid) {
        return 'Aadhaar Number is Invalid';
      }
    }

    return '';
  };

  const errorMessage = getErrorMessage();
  const isValidAadhaar = aadhaarNumber.length === 12 && validateAadhaar(aadhaarNumber).isValid;

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 12);
    setAadhaarNumber(val);
    if (!touched) {
      setTouched(true);
    }
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const handleContinue = () => {
    setTouched(true);
    if (isValidAadhaar && authenticationType && onContinue) {
      onContinue();
    }
  };

  return (
    <Box>
      {/* Aadhaar UID Number */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="primary" fontWeight="500" gutterBottom>
          Aadhaar UID Number *
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter Aadhaar Number"
          value={aadhaarNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          variant="outlined"
          inputProps={{ maxLength: 12, style: { padding: "12px 16px" } }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
        />
      </Box>

      {/* Authentication Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="primary" fontWeight="500" gutterBottom>
          Select Authentication Type *
        </Typography>
        <FormControl component="fieldset" sx={{ width: "100%", mt: 2 }}>
          <RadioGroup
            value={authenticationType}
            onChange={(e) => setAuthenticationType(e.target.value)}
            sx={{ gap: 1 }}
          >
            {/* Demographic Input Option */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                border: authenticationType === "Demographic" ? "2px solid #1976d2" : "1px solid #e0e0e0",
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": { borderColor: "#1976d2" },
              }}
              onClick={() => setAuthenticationType("Demographic")}
            >
              <FormControlLabel
                value="Demographic"
                control={<Radio sx={{ display: "none" }} />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                    <QrCodeIcon sx={{ color: "#666", fontSize: 24 }} />
                    <Typography variant="body2" fontWeight="500">Demographic Input</Typography>
                    <Box sx={{ ml: "auto" }}>
                      <Radio checked={authenticationType === "Demographic"} value="Demographic" size="small" />
                    </Box>
                  </Box>
                }
                sx={{ m: 0, width: "100%", "& .MuiFormControlLabel-label": { width: "100%" } }}
              />
            </Paper>

            {/* OTP Option */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                border: authenticationType === "OTP" ? "2px solid #1976d2" : "1px solid #e0e0e0",
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": { borderColor: "#1976d2" },
              }}
              onClick={() => setAuthenticationType("OTP")}
            >
              <FormControlLabel
                value="OTP"
                control={<Radio sx={{ display: "none" }} />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                    <SmsIcon sx={{ color: "#666", fontSize: 24 }} />
                    <Typography variant="body2" fontWeight="500">OTP on Registered Number</Typography>
                    <Box sx={{ ml: "auto" }}>
                      <Radio checked={authenticationType === "OTP"} value="OTP" size="small" />
                    </Box>
                  </Box>
                }
                sx={{ m: 0, width: "100%", "& .MuiFormControlLabel-label": { width: "100%" } }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Action Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button
          variant="contained"
          disabled={!authenticationType || !isValidAadhaar || isLoading}
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={handleContinue}
        >
          {isLoading && <CircularProgress size={20} color="inherit" />}
          {isLoading ? "Processing..." : "Continue"}
        </Button>
      </Box>
    </Box>
  );
};

export default AadhaarEntryForm;

